"use client";

import { useState } from "react";
import { BeforeAfter } from "@/components/BeforeAfter";
import { BlobImage } from "@/components/BlobImage";
import { primaryButtonClass } from "@/components/form";
import { ProgressBar } from "@/components/ProgressBar";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { useImagePreview } from "@/hooks/useImagePreview";
import { imageSizeOf, useImageSizes } from "@/hooks/useImageSizes";
import { useToolFiles } from "@/hooks/useToolFiles";
import { formatBytes } from "@/lib/files";
import type { ImageJob, ImageOutput } from "@/lib/image/job";
import type { Size } from "@/lib/image/size";
import type { Tool } from "@/lib/tools";
import { runImageJob } from "@/workers/image-client";

/** What to do with one image, or why it can't be done with these settings. */
export type ImagePlan =
  | {
      job: ImageJob;
      /** Output file name, e.g. outputName(file.name, "1200x900", "jpg"). */
      name: (out: ImageOutput) => string;
    }
  | { error: string };

type Props = {
  tool: Tool;
  zipName: string;
  plan: (file: File, size: Size) => ImagePlan;
  /** The settings panel; gets the image being previewed and its size. */
  settings: (ctx: { file?: File; source?: Size }) => React.ReactNode;
  /** Text for the action button, e.g. n => `Resize ${n} images`. */
  actionLabel: (count: number) => string;
  /** Shown while a batch runs, e.g. "Resizing". */
  busyVerb: string;
  /** Extra line under "New …" in the preview, e.g. the quality used. */
  previewNote?: (out: ImageOutput, file: File) => React.ReactNode;
  /** Replaces the before/after slider in the Adjust step (Crop's editor). */
  editor?: (ctx: { file: File; source: Size }) => React.ReactNode;
  /** Show the result on its own instead of a before/after slider. */
  showResultOnly?: boolean;
};

/** The Adjust/Download workspace shared by Resize, Convert, Compress and Crop:
 *  file list, live before/after preview, settings, and a batch run (FR-24, FR-25). */
export function ImageTool({
  tool,
  zipName,
  plan,
  settings,
  actionLabel,
  busyVerb,
  previewNote,
  editor,
  showResultOnly = false,
}: Props) {
  const { files, setFiles, notices } = useToolFiles(tool);
  const sizes = useImageSizes(files);
  const [selected, setSelected] = useState(0);
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [failures, setFailures] = useState<string[]>([]);

  const index = Math.min(selected, Math.max(0, files.length - 1));
  const file = files[index];
  const source = file ? sizes.get(file) : undefined;
  const current = file && source ? plan(file, source) : null;
  const preview = useImagePreview(
    file,
    current && "job" in current ? current.job : null,
  );
  const error = current && "error" in current ? current.error : null;
  const out = preview.output;
  const count = files.length;

  async function run() {
    setFailures([]);
    setProgress({ done: 0, total: count });
    const done: ToolResult[] = [];
    const failed: string[] = [];
    for (const [i, f] of files.entries()) {
      try {
        const size = sizes.get(f) ?? (await imageSizeOf(f));
        if (!size) throw new Error("This image couldn't be opened.");
        const p = plan(f, size);
        if ("error" in p) throw new Error(p.error);
        const result = await runImageJob(f, p.job);
        done.push({
          blob: result.blob,
          name: p.name(result),
          detail: `${result.width} × ${result.height} px${
            result.fits ? "" : " · couldn't reach the size limit"
          }`,
        });
      } catch (e) {
        failed.push(`${f.name}: ${(e as Error).message}`);
      }
      setProgress({ done: i + 1, total: count });
    }
    setProgress(null);
    setFailures(failed);
    if (done.length) setResults(done);
  }

  const singleResult = results?.length === 1 ? results[0] : null;

  return (
    <ToolShell
      tool={tool}
      files={files}
      onFilesChange={(next) => {
        setFiles(next);
        setResults(null);
      }}
      notices={[...notices, ...failures]}
      results={results}
      onStartOver={() => {
        setFiles([]);
        setResults(null);
        setFailures([]);
        setSelected(0);
      }}
      zipName={zipName}
      fileDetails={files.map((f) => {
        const size = sizes.get(f);
        return size ? `${size.width} × ${size.height} px` : undefined;
      })}
      selectedFile={index}
      onSelectFile={setSelected}
      resultPreview={
        singleResult && file ? (
          showResultOnly ? (
            <ResultImage blob={singleResult.blob} />
          ) : (
            <BeforeAfter before={file} after={singleResult.blob} />
          )
        ) : null
      }
    >
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(320px,380px)] lg:items-start">
        <section
          aria-label="Preview"
          className="flex flex-col gap-4 rounded-[16px] border bg-card p-4 sm:p-5"
        >
          {editor && file && source ? (
            editor({ file, source })
          ) : file && out ? (
            <BeforeAfter before={file} after={out.blob} dim={preview.busy} />
          ) : (
            <div className="flex aspect-[4/3] items-center justify-center rounded-[10px] bg-[#e6e9f2] p-4 text-center text-muted-foreground">
              {preview.error ??
                (error
                  ? "Fix the settings to see a preview"
                  : "Making a preview…")}
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-[10px] bg-canvas p-3.5">
              <p className="truncate text-[13px] text-muted-foreground">
                Original · {file?.name}
              </p>
              <p className="text-lg font-semibold">
                {source ? `${source.width} × ${source.height} px` : "…"} ·{" "}
                {file ? formatBytes(file.size) : ""}
              </p>
            </div>
            <div
              className="rounded-[10px] bg-brand-tint p-3.5"
              aria-live="polite"
            >
              <p className="text-[13px] text-brand-strong">
                New{preview.busy ? " · updating…" : ""}
              </p>
              <p className="text-lg font-semibold" data-testid="new-size">
                {out
                  ? `${out.width} × ${out.height} px · ${formatBytes(out.blob.size)}`
                  : "…"}
              </p>
              {out && !out.fits && (
                <p className="text-sm text-ink">
                  Even at the lowest quality it&apos;s over the limit. Try a
                  smaller size.
                </p>
              )}
              {out && file && previewNote?.(out, file)}
            </div>
          </div>
        </section>

        <aside
          aria-label="Settings"
          className="flex min-w-0 flex-col gap-5 rounded-[16px] border bg-card p-4 sm:p-5"
        >
          {settings({ file, source })}

          {error && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error}
            </p>
          )}

          {/* On phones the button stays in reach at the bottom of the screen. */}
          <div className="sticky bottom-0 -mx-4 -mb-4 flex flex-col gap-2 border-t bg-card p-4 sm:-mx-5 sm:-mb-5 sm:p-5 lg:static lg:m-0 lg:border-0 lg:p-0">
            {progress ? (
              <ProgressBar
                label={`${busyVerb} ${progress.done} of ${progress.total}…`}
                value={progress.done}
                max={progress.total}
              />
            ) : (
              <button
                type="button"
                onClick={run}
                disabled={!!error || count === 0}
                className={primaryButtonClass}
              >
                {actionLabel(count)}
              </button>
            )}
            <p className="text-center text-[13px] text-muted-foreground">
              {count > 1 ? "Downloads as a ZIP · " : ""}nothing is uploaded
            </p>
          </div>
        </aside>
      </div>
    </ToolShell>
  );
}

function ResultImage({ blob }: { blob: Blob }) {
  return (
    <div className="flex max-h-[420px] justify-center rounded-[10px] bg-[#e6e9f2] p-3">
      <BlobImage
        blob={blob}
        alt="Result"
        className="max-h-[396px] max-w-full object-contain"
      />
    </div>
  );
}
